import type { ActionType, AssessmentStatusFilter, BusinessCriticalityFilter, FilterKey } from './types';

// Feature flag for mock data
export const USE_MOCK_DATA = true; // Toggle when API ready

// Pagination defaults
export const DEFAULT_PAGE_SIZE = 25;
export const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

// Risk levels for filter dropdown (lowercase values to match FilterKey)
export const RISK_LEVELS: { value: FilterKey; label: string }[] = [
  { value: 'all_risks', label: 'All Risk' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

// Status filter options for dropdown
export const STATUS_FILTER_OPTIONS: { value: AssessmentStatusFilter | 'all'; label: string }[] = [
  { value: 'all', label: 'Status' },
  { value: 'intake_form', label: 'Intake Form' },
  { value: 'information_gathering', label: 'Information Gathering' },
  { value: 'generating_results', label: 'Generating Results' },
  { value: 'needs_review', label: 'Needs Review' },
  { value: 'complete', label: 'Completed' }
];

// Business criticality filter options for dropdown
export const BUSINESS_CRITICALITY_OPTIONS: { value: BusinessCriticalityFilter | 'all'; label: string }[] = [
  { value: 'all', label: 'Business Criticality' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

// Action menu items
export const ACTIONS: { key: ActionType; label: string; icon: string }[] = [
  { key: 'view_assessment', label: 'View Assessment', icon: 'ArrowRight' },
  { key: 'download_report', label: 'Download Report', icon: 'Download' },
  { key: 'download_summary', label: 'Download Summary', icon: 'Download' },
  //
  { key: 'version_history', label: 'Version History', icon: 'History' },
];

// Risk badge styles (Title Case keys to match RiskLevel type)
export const RISK_STYLES = {
  High: {
    bg: 'bg-red-100',
    text: 'text-red-600',
    border: 'border-red-200',
  },
  Medium: {
    bg: 'bg-gray-100',
    text: 'text-gray-600',
    border: 'border-gray-200',
  },
  Low: {
    bg: 'bg-green-100',
    text: 'text-green-600',
    border: 'border-green-200',
  },
} as const;

// Status styles (snake_case keys to match AssessmentStatus type)
export const STATUS_STYLES = {
  needs_review: 'text-blue-600 hover:underline cursor-pointer',
  in_progress: 'text-[#314158] font-normal text-sm leading-5',
  reassessment_in_progress: 'text-[#314158] font-normal text-sm leading-5',
  input_needed: 'text-[#314158] font-normal text-sm leading-5',
  form_sent: 'text-[#314158] font-normal text-sm leading-5',
  complete: 'text-[#314158] font-normal text-sm leading-5',
  new_request: 'text-[#314158] font-normal text-sm leading-5',
} as const;

// Status display labels (for UI)
export const STATUS_LABELS = {
  needs_review: 'Need Review',
  in_progress: 'In Progress',
  reassessment_in_progress: 'In Progress', //reassessment
  input_needed: 'In Progress', //intake form
  form_sent: 'In Progress', //due diligence
  complete: 'Completed',
  new_request: 'New Request',
} as const;

// Animation config
export const EXPAND_ANIMATION = {
  duration: 0.2,
  ease: [0.4, 0, 0.2, 1], // Material Design easing
} as const;

// Search debounce delay (ms)
export const SEARCH_DEBOUNCE_MS = 300;

// Version status badge styles
export const VERSION_STATUS_STYLES = {
  active: {
    bg: 'bg-[#E8FAF0]',
    text: 'text-[#1FCB6A]',
    label: 'Active',
  },
  archived: {
    bg: 'bg-[#E6F1FE]',
    text: 'text-[#006FEE]',
    label: 'Archived',
  },
  completed: {
    bg: 'bg-[#E6F1FE]',
    text: 'text-[#006FEE]',
    label: 'Completed',
  },
} as const;

// Assessment step names for progress display
export const ASSESSMENT_STEP_NAMES: Record<number, string> = {
  1: 'Intake Form',
  2: 'Information Gathering',
  3: 'Generating Results',
  4: 'Review Results',
  5: 'Completed',
};
